import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { FC } from 'react';
import {
  PackageInStreamFragmentFragment,
  StreamQueryQuery,
  StreamState,
  StreamType,
} from '@/__generated__/graphql.ts';
import { LayoutCard } from '@/components/layout/card.tsx';
import { StreamLabel } from '@/components/post-label/stream-label.tsx';
import { BannerSocialActions } from '@/components/streams/stream-banner/banner-social-actions.tsx';
import { hasRightsToStream } from '@/components/streams/stream-banner/has-rights-to-stream';
import { StreamSoundButton } from '@/components/streams/toggle-notif-sound/stream-sound-button.tsx';
import { ChoosePackageModal } from '@/components/streams/toggle-paid-stream-subscription/stream-included-in-package-modal.tsx';
import { IncludedInPackageLabel } from '@/routes/_app.p.streams_.$id/stream-banner/included-in-package-label.tsx';
import { MeStreamActions } from '@/routes/_app.p.streams_.$id/stream-banner/me-stream-actions.tsx';
import { StreamBannerContent } from '@/routes/_app.p.streams_.$id/stream-banner/stream-banner-content.tsx';
import {
  buttonsBlock,
  freeBannerActionsBlock,
  followStreamButton,
  labelContainer,
  labelsContainer,
  freeStreamBannerFooter,
  streamBannerCard,
} from '@/routes/_app.p.streams_.$id/stream-banner/stream-banner.css.ts';

export const FreeStreamBanner: FC<{
  stream: StreamQueryQuery['stream'];
  isMe: boolean;
  toggleFollow: () => void;
  followLoading?: boolean;
}> = ({ stream, isMe, toggleFollow, followLoading }) => {
  const isTablet = useIsTablet();

  if (!stream) {
    return null;
  }

  const hasRights = hasRightsToStream(stream);

  const isDraft = stream.streamState === StreamState.Draft;

  const showIncludedInPackageLabel =
    stream.inPackages?.length && (!isTablet || (!isMe && !hasRights));

  const showSoundButton =
    stream.accessAllowed &&
    (!stream.customTargetsData || stream.customTargets?.length);

  return (
    <LayoutCard className={streamBannerCard}>
      <div>
        <StreamBannerContent stream={stream} isMe={isMe} />
        <div className={labelContainer}>
          <StreamLabel label={stream.label} streamId={stream.id} />
        </div>
      </div>
      <div className={freeStreamBannerFooter}>
        <BannerSocialActions
          streamId={stream.id}
          followersCount={stream.followersCount}
          isPrivate={stream.streamType === StreamType.Private}
          isAuthor={isMe}
          isDraft={isDraft}
        />

        <div className={freeBannerActionsBlock}>
          {showIncludedInPackageLabel ? (
            <div className={labelsContainer}>
              <IncludedInPackageLabel
                packages={stream.inPackages}
                packageOnly={stream.packageOnly}
              />
            </div>
          ) : null}

          <div className={buttonsBlock}>
            {showSoundButton ? (
              <StreamSoundButton streamId={stream.id} />
            ) : null}

            {!isMe ? (
              <FollowFreeStreamAction
                following={stream.following}
                toggleFollow={toggleFollow}
                followLoading={followLoading}
                inPackages={stream.inPackages}
                isDraft={isDraft}
                activeSubscription={hasRights}
                packageOnly={stream.packageOnly}
              />
            ) : (
              <MeStreamActions stream={stream} />
            )}
          </div>
        </div>
      </div>
    </LayoutCard>
  );
};

const FollowFreeStreamAction: FC<{
  packageOnly: boolean;
  following: boolean;
  activeSubscription: boolean;
  inPackages: PackageInStreamFragmentFragment[];
  isDraft: boolean;
  toggleFollow: () => void;
  followLoading?: boolean;
}> = ({
  packageOnly,
  following,
  activeSubscription,
  inPackages,
  isDraft,
  toggleFollow,
  followLoading,
}) => {
  const isTablet = useIsTablet();

  if (packageOnly && !activeSubscription) {
    return null;
  }

  return inPackages?.length && !following && !activeSubscription ? (
    <ChoosePackageModal
      trigger={
        <Button
          variant={'primary'}
          size={isTablet ? 'medium' : 'small'}
          className={followStreamButton}
          disabled={isDraft && !following}
          data-testid={'follow-stream-button'}
          loading={followLoading}
        >
          Follow
        </Button>
      }
      packages={inPackages}
      title={'Follow stream'}
      actionTitle={'Follow stream only'}
      onSubmit={toggleFollow}
    />
  ) : (
    <Button
      onClick={toggleFollow}
      variant={'primary'}
      size={isTablet ? 'medium' : 'small'}
      className={followStreamButton}
      disabled={isDraft && !following}
      data-testid={'follow-stream-button'}
      loading={followLoading}
    >
      {!following ? 'Follow' : 'Following'}
    </Button>
  );
};
